import { Fragment, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import TableRow from "@mui/material/TableRow";
import { useNavigate } from "react-router-dom";
import { useTranslationApp } from "../../../../lib/i18next";
import AdMediaPreviewIcon from "../../../../components/Common/AdMediaPreviewIcon";
import TableCell from "../../../BillingAndPayments/components/TableCell";
import Button from "../../../../components/Buttons/Button";
import Loader from "../../../../components/Loaders/Loader";
import {
  commonPropsTableCellBody,
  SkeletonCampaignMetric,
} from "../../../../components/Tables/campaignsTableShared";
import { logoPlatform } from "../../../../utils/string";
import {
  ADS_CAMPAIGN_STATUS_ACTIVE,
  ADS_CAMPAIGN_STATUS_DELETED,
  META,
} from "../../../../utils/constants";
import {
  AD,
  AD_GROUP,
  ASSETS_GROUP,
  BUTTON_RETRY,
  CAMPAIGNS_TABLE_COLLAPSE_GROUP,
  CAMPAIGNS_TABLE_EXPAND_GROUP,
  CAMPAIGNS_TABLE_SHOW_MORE_ADS,
  CAMPAIGNS_TABLE_TREE_ADS_EMPTY,
  CAMPAIGNS_TABLE_TREE_EMPTY,
  CAMPAIGNS_TABLE_TREE_ERROR,
  CAMPAIGNS_TABLE_TREE_METRICS_ERROR,
  DATE_SHORT_2,
  LOADING,
} from "../../../../i18n/keysTranslations";
import { resolveCampaignLevelStatistics } from "../../../NewAdsCampaign/components/Common/campaignLevelMetrics";
import StatusSwitch from "../StatusSwitch";
import CampaignTableExpandButton from "./CampaignTableExpandButton";
import EntityMetricValue from "./EntityMetricValue";
import {
  CAMPAIGN_TABLE_ADS_PAGE_SIZE,
  CAMPAIGN_TABLE_ENTITY_KIND,
  CAMPAIGN_TABLE_LOAD_PHASE,
  CAMPAIGN_TABLE_NAME_INDENT,
  CAMPAIGN_TABLE_NESTED_STICKY_Z_INDEX,
  CAMPAIGN_TABLE_TREE_VIEW,
  buildCampaignEditorPath,
  buildCampaignTableGroups,
  getCampaignTableEntityCreationTime,
  getCampaignTableEntityName,
  getCampaignTableTreeView,
  isCampaignTableStatisticsLoading,
  toCampaignTableDate,
} from "./campaignTableTree";

const METRIC_COLUMNS = [
  { visibility: "impressions", type: "impressions" },
  { visibility: "clicks", type: "clicks" },
  { visibility: "ctr", type: "ctr" },
  { visibility: "spend", type: "spend" },
  { visibility: "roas", type: "roas" },
  { visibility: "cpm", type: "cpm" },
  { visibility: "cpc", type: "cpc" },
  { visibility: "results", type: "mainAction" },
  { visibility: "cpa", type: "cpa" },
  { visibility: "frequency", type: "frequency" },
];

const getResolvedStatistics = ({
  statistics,
  platform,
  bucket,
  entityID,
  campaign,
}) =>
  resolveCampaignLevelStatistics({
    raw: statistics?.[platform]?.[bucket]?.[entityID] || {},
    platform,
    conversionActions: campaign?.platformsData?.[platform]?.conversionActions,
  }).statistics;

const TreeMessageRow = ({ message = "", isLastRow = false, action = null }) => (
  <TableRow>
    <TableCell
      {...commonPropsTableCellBody}
      colSpan={18}
      align="center"
      isLastRow={isLastRow}
      showTooltip={false}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          py: 0.5,
        }}
      >
        {message ? (
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        ) : null}
        {action}
      </Box>
    </TableCell>
  </TableRow>
);

const PlatformMark = ({ platform }) => (
  <Avatar
    src={logoPlatform(platform)}
    alt={`${platform || ""} logo`}
    variant="square"
    sx={{
      width: platform === META ? 25 : 20,
      height: "auto",
      ".MuiAvatar-img": { objectFit: "contain" },
    }}
  />
);

const EntityCells = ({
  level,
  label,
  platform,
  status,
  statistics,
  statisticsLoading = false,
  creatorName = null,
  creationTime = null,
  columnVisibility,
  statusColumnWidth,
  alertColumnWidth,
  isLastRow,
  canEditCampaign,
  onChangeStatus,
  onOpenEditor = () => {},
  mediaUrls = null,
  namePrefix = null,
}) => {
  const { t } = useTranslationApp();
  const rowBackground = level === 3 ? "#f3f3f3" : "#f7f7f7";
  const nameIndent =
    level === 3
      ? CAMPAIGN_TABLE_NAME_INDENT.AD
      : CAMPAIGN_TABLE_NAME_INDENT.AD_GROUP;
  // Normalized once here: passing a raw value into t() with a date format would
  // make date-fns throw on an invalid date and break the whole table.
  const creationDate = toCampaignTableDate(creationTime);

  return (
    <TableRow
      hover
      onClick={(event) => {
        if (event.defaultPrevented) return;
        onOpenEditor();
      }}
      sx={{
        cursor: "pointer",
        "& td": {
          backgroundColor: `${rowBackground} !important`,
        },
        "&:hover td": {
          backgroundColor: "#ececec !important",
        },
      }}
    >
      <TableCell
        {...commonPropsTableCellBody}
        showTooltip={false}
        isLastRow={isLastRow}
        isSticky={true}
        stickyPosition="left"
        stickyOffset={0}
        sx={{
          width: `${statusColumnWidth}px`,
          minWidth: `${statusColumnWidth}px`,
          maxWidth: `${statusColumnWidth}px`,
          pl: "12px !important",
          pr: "0px !important",
          overflow: "visible",
          zIndex: CAMPAIGN_TABLE_NESTED_STICKY_Z_INDEX,
        }}
      >
        <StatusSwitch
          status={status}
          active={status === ADS_CAMPAIGN_STATUS_ACTIVE}
          readOnly={!canEditCampaign}
          onChangeStatus={onChangeStatus}
        />
      </TableCell>
      <TableCell
        {...commonPropsTableCellBody}
        showTooltip={false}
        isLastRow={isLastRow}
        isSticky={true}
        stickyPosition="left"
        stickyOffset={statusColumnWidth}
        sx={{
          width: `${alertColumnWidth}px`,
          minWidth: `${alertColumnWidth}px`,
          maxWidth: `${alertColumnWidth}px`,
          px: "0px !important",
          zIndex: CAMPAIGN_TABLE_NESTED_STICKY_Z_INDEX,
        }}
      />
      <TableCell
        {...commonPropsTableCellBody}
        showTooltip={false}
        isLastRow={isLastRow}
        isSticky={true}
        stickyPosition="left"
        stickyOffset={statusColumnWidth + alertColumnWidth}
        sx={{
          pl: "16px !important",
          minWidth: 250,
          width: 250,
          maxWidth: 250,
          zIndex: CAMPAIGN_TABLE_NESTED_STICKY_Z_INDEX,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
            pl: nameIndent,
          }}
        >
          {namePrefix || <Box sx={{ width: 28, flexShrink: 0 }} />}
          {level === 3 ? (
            <Box sx={{ display: "flex", flexShrink: 0 }}>
              <AdMediaPreviewIcon mediaUrls={mediaUrls || []} size={20} />
            </Box>
          ) : null}
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
        </Box>
      </TableCell>
      <TableCell
        {...commonPropsTableCellBody}
        isLastRow={isLastRow}
        showTooltip={false}
      >
        <PlatformMark platform={platform} />
      </TableCell>
      {METRIC_COLUMNS.map((column) =>
        columnVisibility?.[column.visibility] ? (
          <TableCell
            key={column.type}
            {...commonPropsTableCellBody}
            isLastRow={isLastRow}
            showTooltip={false}
          >
            {statisticsLoading ? (
              <SkeletonCampaignMetric />
            ) : (
              <EntityMetricValue
                statistics={statistics}
                type={column.type}
                status={status}
              />
            )}
          </TableCell>
        ) : null
      )}
      <TableCell
        {...commonPropsTableCellBody}
        isLastRow={isLastRow}
        showTooltip={false}
      >
        {creatorName || t(LOADING)}
      </TableCell>
      <TableCell
        {...commonPropsTableCellBody}
        isLastRow={isLastRow}
        showTooltip={false}
      >
        {creationDate ? t(DATE_SHORT_2, { date: creationDate }) : ""}
      </TableCell>
      <TableCell
        {...commonPropsTableCellBody}
        isLastRow={isLastRow}
        showTooltip={false}
      />
    </TableRow>
  );
};

const CampaignTableTreeRows = ({
  campaignID,
  entry,
  columnVisibility,
  statusColumnWidth,
  alertColumnWidth,
  canEditCampaign,
  isLastRow = false,
  creatorName = null,
  onRetry,
  onChangeEntityStatus,
}) => {
  const { t } = useTranslationApp();
  const navigate = useNavigate();
  const [openGroups, setOpenGroups] = useState({});
  const [visibleAdsByGroup, setVisibleAdsByGroup] = useState({});

  const retryButton = (
    <Button
      color="secondary"
      onClick={(event) => {
        event.stopPropagation();
        onRetry({ campaignID });
      }}
      sx={{ minWidth: 0, height: 32, py: 0 }}
    >
      {t(BUTTON_RETRY)}
    </Button>
  );

  const retryStatisticsButton = (
    <Button
      color="secondary"
      onClick={(event) => {
        event.stopPropagation();
        onRetry({ campaignID, scope: "statistics" });
      }}
      sx={{ minWidth: 0, height: 32, py: 0 }}
    >
      {t(BUTTON_RETRY)}
    </Button>
  );

  // The entity (ad groups + ads) and its statistics load independently. Only
  // when there is no tree to render yet does the row keep the loader. Once the
  // tree is ready the structure renders immediately and just the metric cells
  // wait behind skeletons.
  const treeView = getCampaignTableTreeView(entry);

  if (treeView === CAMPAIGN_TABLE_TREE_VIEW.LOADER) {
    return (
      <TableRow>
        <TableCell
          {...commonPropsTableCellBody}
          colSpan={18}
          align="center"
          isLastRow={isLastRow}
          showTooltip={false}
        >
          <Loader size={24} hasMarginTop={false} />
        </TableCell>
      </TableRow>
    );
  }

  if (treeView === CAMPAIGN_TABLE_TREE_VIEW.ERROR) {
    return (
      <TreeMessageRow
        message={t(CAMPAIGNS_TABLE_TREE_ERROR)}
        isLastRow={isLastRow}
        action={retryButton}
      />
    );
  }

  const groups = buildCampaignTableGroups({
    campaign: entry.campaign,
    deletedStatus: ADS_CAMPAIGN_STATUS_DELETED,
  });

  if (groups.length === 0) {
    return (
      <TreeMessageRow
        message={t(CAMPAIGNS_TABLE_TREE_EMPTY)}
        isLastRow={isLastRow}
      />
    );
  }

  const lastGroupID = groups[groups.length - 1]?.id;
  const statisticsLoading = isCampaignTableStatisticsLoading(entry.statsPhase);
  const statisticsFailed = entry.statsPhase === CAMPAIGN_TABLE_LOAD_PHASE.ERROR;

  return (
    <>
      {statisticsFailed ? (
        <TreeMessageRow
          message={t(CAMPAIGNS_TABLE_TREE_METRICS_ERROR)}
          action={retryStatisticsButton}
        />
      ) : null}
      {groups.map((group, groupIndex) => {
        const isAdGroup = group.kind === CAMPAIGN_TABLE_ENTITY_KIND.AD_GROUP;
        const isOpen = Boolean(openGroups[group.id]);
        const visibleCount =
          visibleAdsByGroup[group.id] || CAMPAIGN_TABLE_ADS_PAGE_SIZE;
        const visibleAds = isOpen ? group.ads.slice(0, visibleCount) : [];
        const hiddenCount = isOpen ? group.ads.length - visibleAds.length : 0;
        const showEmptyAds = isOpen && group.ads.length === 0;
        const isLastGroup = group.id === lastGroupID;
        const groupIsLastRendered = isLastRow && isLastGroup && !isOpen;
        const groupLabel = getCampaignTableEntityName({
          entity: group,
          fallback: `${t(
            group.kind === CAMPAIGN_TABLE_ENTITY_KIND.ASSETS_GROUP
              ? ASSETS_GROUP
              : AD_GROUP
          )} ${groupIndex + 1}`,
        });
        const groupStatistics = getResolvedStatistics({
          statistics: entry.statistics,
          platform: group.platform,
          bucket: isAdGroup ? "adsGroups" : "assetsGroups",
          entityID: group.id,
          campaign: entry.campaign,
        });

        return (
          <Fragment key={group.id}>
            <EntityCells
              level={2}
              label={groupLabel}
              platform={group.platform}
              status={group.status}
              statistics={groupStatistics}
              statisticsLoading={statisticsLoading}
              creatorName={creatorName}
              creationTime={getCampaignTableEntityCreationTime({
                entity: group,
                campaign: entry.campaign,
              })}
              columnVisibility={columnVisibility}
              statusColumnWidth={statusColumnWidth}
              alertColumnWidth={alertColumnWidth}
              isLastRow={groupIsLastRendered}
              canEditCampaign={canEditCampaign}
              namePrefix={
                isAdGroup ? (
                  <CampaignTableExpandButton
                    expanded={isOpen}
                    expandLabel={t(CAMPAIGNS_TABLE_EXPAND_GROUP)}
                    collapseLabel={t(CAMPAIGNS_TABLE_COLLAPSE_GROUP)}
                    onToggle={() => {
                      setOpenGroups((current) => ({
                        ...current,
                        [group.id]: !current[group.id],
                      }));
                    }}
                  />
                ) : null
              }
              onChangeStatus={() => {
                onChangeEntityStatus({
                  campaignID,
                  platform: group.platform,
                  kind: group.kind,
                  entityID: group.id,
                  name: groupLabel,
                  status: group.status,
                });
              }}
              onOpenEditor={() => {
                navigate(
                  buildCampaignEditorPath({
                    campaignID,
                    platform: group.platform,
                    adGroupID: isAdGroup ? group.id : undefined,
                    assetsGroupID: isAdGroup ? undefined : group.id,
                  })
                );
              }}
            />
            {visibleAds.map((ad, adIndex) => {
              const adLabel = getCampaignTableEntityName({
                entity: ad,
                fallback: `${t(AD)} ${adIndex + 1}`,
              });
              const adIsLast =
                isLastRow &&
                isLastGroup &&
                hiddenCount === 0 &&
                adIndex === visibleAds.length - 1;

              return (
                <EntityCells
                  key={ad.id}
                  level={3}
                  label={adLabel}
                  platform={ad.platform || group.platform}
                  status={ad.status}
                  statistics={getResolvedStatistics({
                    statistics: entry.statistics,
                    platform: ad.platform || group.platform,
                    bucket: "ads",
                    entityID: ad.id,
                    campaign: entry.campaign,
                  })}
                  statisticsLoading={statisticsLoading}
                  creatorName={creatorName}
                  creationTime={getCampaignTableEntityCreationTime({
                    entity: ad,
                    campaign: entry.campaign,
                  })}
                  columnVisibility={columnVisibility}
                  statusColumnWidth={statusColumnWidth}
                  alertColumnWidth={alertColumnWidth}
                  isLastRow={adIsLast}
                  canEditCampaign={canEditCampaign}
                  mediaUrls={ad.mediaUrls}
                  onOpenEditor={() => {
                    navigate(
                      buildCampaignEditorPath({
                        campaignID,
                        platform: ad.platform || group.platform,
                        adGroupID: group.id,
                        adID: ad.id,
                      })
                    );
                  }}
                  onChangeStatus={() => {
                    onChangeEntityStatus({
                      campaignID,
                      platform: ad.platform || group.platform,
                      kind: CAMPAIGN_TABLE_ENTITY_KIND.AD,
                      entityID: ad.id,
                      adGroupID: group.id,
                      name: adLabel,
                      status: ad.status,
                    });
                  }}
                />
              );
            })}
            {showEmptyAds ? (
              <TreeMessageRow
                message={t(CAMPAIGNS_TABLE_TREE_ADS_EMPTY)}
                isLastRow={isLastRow && isLastGroup}
              />
            ) : null}
            {hiddenCount > 0 ? (
              <TreeMessageRow
                isLastRow={isLastRow && isLastGroup}
                action={
                  <Button
                    color="secondary"
                    onClick={() => {
                      setVisibleAdsByGroup((current) => ({
                        ...current,
                        [group.id]: visibleCount + CAMPAIGN_TABLE_ADS_PAGE_SIZE,
                      }));
                    }}
                    sx={{ minWidth: 0, height: 32, py: 0 }}
                  >
                    {t(CAMPAIGNS_TABLE_SHOW_MORE_ADS, { count: hiddenCount })}
                  </Button>
                }
              />
            ) : null}
          </Fragment>
        );
      })}
    </>
  );
};

export default CampaignTableTreeRows;
